"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  OrganizationBranding,
  organizationBrandingRepository,
} from "@/repository/organizationBrandingRepository";
import { localAuthRepository } from "@/repository/localAuthRepository";
import { isTenantStaffRole } from "@/lib/staffPermissions";
import { useStaffMode } from "@/contexts/StaffModeContext";

type OrganizationBrandingContextValue = {
  branding: OrganizationBranding | null;
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  updateBranding: (branding: OrganizationBranding) => void;
};

const OrganizationBrandingContext =
  createContext<OrganizationBrandingContextValue | null>(null);

export function OrganizationBrandingProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [branding, setBranding] = useState<OrganizationBranding | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { mode } = useStaffMode();

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    const role = localAuthRepository.getUserInfo()?.role;
    if (mode === "platform" || !isTenantStaffRole(role)) {
      setBranding(null);
      setIsLoading(false);
      return;
    }

    try {
      setBranding(await organizationBrandingRepository.findCurrent());
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause
          : new Error("組織情報の取得に失敗しました")
      );
    } finally {
      setIsLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<OrganizationBrandingContextValue>(
    () => ({
      branding,
      isLoading,
      error,
      refresh,
      updateBranding: setBranding,
    }),
    [branding, error, isLoading, refresh]
  );

  return (
    <OrganizationBrandingContext.Provider value={value}>
      {children}
    </OrganizationBrandingContext.Provider>
  );
}

export function useOrganizationBranding() {
  const context = useContext(OrganizationBrandingContext);
  if (!context) {
    throw new Error(
      "useOrganizationBranding must be used within OrganizationBrandingProvider"
    );
  }
  return context;
}
